import { useFormikEnhanced } from '@superdispatch/forms';
import { Stack, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { FormikProvider } from 'formik';
import { useMemo } from 'react';
import { LocalStore } from 'shared/helpers/Store';
import { useLoadRequestSuggestionsNudging } from 'shared/modules/load-suggestions/LoadSuggestionsNudgingHelper';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { usePostingAPI } from '../../data/LoadboardPostingAPI';
import { PostingCarrierLoadDTO, PostingDTO } from '../../data/dto/PostingDTO';
import {
  PostingRequestValuesDTO,
  postingRequestPayloadSchema,
  postingRequestValuesSchema,
} from '../../data/dto/PostingRequestDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { PostingVehicleStatus } from './PostingVehicleStatus';
import { PostingWaiver } from './PostingWaiver';
import { usePostingsPageContext } from './PostingsPageContext';
import { RequestFormFields } from './RequestFormFields';

interface UpdateRequestFormProps {
  posting: PostingDTO;
  carrierPostingInfo: PostingCarrierLoadDTO;
}

export function UpdateRequestForm({
  posting,
  carrierPostingInfo,
}: UpdateRequestFormProps) {
  const { load } = posting;
  const {
    router: { closePostingScreen },
  } = useLoadboardContext();
  const { addSnackbar } = useSnackbarStack();
  const {
    params: { search_criteria, current_tab, current_saved_search_guid },
  } = usePostingsPageContext();
  const { updatePostingRequest, cancelPostingRequest } = usePostingAPI();
  const {
    loadSuggestionsNudgingRequestedLoadGuid,
    setLoadSuggestionsNudgingRequestedLoadGuid,
  } = useLoadRequestSuggestionsNudging();
  const isEditedByShipperFlagEnabled = useFlag('slb_edited_by_shipper');
  const isEditedByShipper =
    isEditedByShipperFlagEnabled && !!carrierPostingInfo.load_edited_at;
  // Location-only edits keep the carrier's requested price.
  const shouldPrefillEditedPrice =
    isEditedByShipper && !!carrierPostingInfo.load_edits?.previous_price;

  const cancelForm = useFormikEnhanced({
    initialValues: {},
    onSubmit() {
      return cancelPostingRequest(carrierPostingInfo.guid);
    },
    onSubmitSuccess() {
      if (loadSuggestionsNudgingRequestedLoadGuid === load.guid) {
        setLoadSuggestionsNudgingRequestedLoadGuid(null);
      }
      closePostingScreen();
      trackLoadboardEvent({
        name: 'SLB: Canceled Load Request',
        load,
        tabName: current_tab,
      });
      addSnackbar('The load request has been canceled', { variant: 'success' });
    },
    onSubmitFailure() {
      addSnackbar('Error while canceling', { variant: 'error' });
    },
  });

  const initialValues = useMemo<PostingRequestValuesDTO>(
    () =>
      postingRequestValuesSchema.cast({
        price: shouldPrefillEditedPrice ? load.price : carrierPostingInfo.price,
        posting_guid: load.posting_guid,
        pickup_date: carrierPostingInfo.pickup_date,
        delivery_date: carrierPostingInfo.delivery_date,
        carrier_contact_name: carrierPostingInfo.carrier_contact_name,
        carrier_comment: carrierPostingInfo.carrier_comment,
      }),
    [
      carrierPostingInfo,
      load.posting_guid,
      load.price,
      shouldPrefillEditedPrice,
    ],
  );
  const updateForm = useFormikEnhanced<
    PostingRequestValuesDTO,
    PostingCarrierLoadDTO
  >({
    initialValues,
    validationSchema: postingRequestValuesSchema,
    onSubmit(values) {
      return updatePostingRequest(
        load.guid,
        carrierPostingInfo.guid,
        postingRequestPayloadSchema.cast(values),
      );
    },
    onSubmitSuccess(_, values) {
      LocalStore.set('last_carrier_contact_name', values.carrier_contact_name);
      addSnackbar('Request updated', { variant: 'success' });

      trackLoadboardEvent({
        posting,
        bookQuery: values,
        tabName: current_tab,
        criteria: search_criteria,
        savedSearchGuid: current_saved_search_guid,
        name: 'SLB: Updated Load Request',
      });
    },
  });

  // Stays off after a successful re-confirm until the refetch clears the edit.
  const canReconfirmEditedLoad =
    isEditedByShipper && updateForm.status.type !== 'submitted';

  return (
    <FormikProvider value={updateForm}>
      <Stack space="small">
        <RequestFormFields load={load} isEditMode={true} />

        <PostingVehicleStatus load={load} />

        <Stack space="xsmall">
          <Button
            type="button"
            onClick={() => {
              updateForm.handleSubmit();
            }}
            disabled={!updateForm.dirty && !canReconfirmEditedLoad}
            pending={updateForm.isSubmitting}
            fullWidth={true}
            size="large"
          >
            Update
          </Button>

          <Button
            type="button"
            size="large"
            fullWidth={true}
            variant="neutral"
            pending={cancelForm.isSubmitting}
            onClick={() => {
              if (
                // eslint-disable-next-line no-alert
                window.confirm('Are you sure you want to cancel this request?')
              ) {
                cancelForm.handleSubmit();
              }
            }}
          >
            Cancel
          </Button>

          <PostingWaiver />
        </Stack>
      </Stack>
    </FormikProvider>
  );
}
